import React from "react";
import { useFela } from "react-fela";

import Oval from "@/icons/oval-copy-10.svg";
import { useTheme } from "@/hooks/useTheme";
import { Grid } from "@/components/Grid";
import { Heading } from "@/components/Heading";
import { LinkButton } from "@/components/LinkButton";

import { ITryBeforeYouBuySection } from "./types";
import { ListElement } from "./ListElement";
import { ListElementWithTitle } from "./ListElementWithTitle";

type IProps = ITryBeforeYouBuySection & JSX.IntrinsicElements["div"];

export const TryBeforeYouBuySection: React.FC<IProps> = ({
  elements = [],
  title,
  cta,
  ...rest
}) => {
  const { css } = useFela();
  const { bp } = useTheme();

  const ListElementComponent = elements?.some(element => element.title)
    ? ListElementWithTitle
    : ListElement;

  return (
    <Grid
      id="TryBeforeYouBuySection"
      customStyle={{
        position: "relative",
        flexDirection: "column",
        [bp.FROM_DESKTOP]: {
          flexDirection: "row",
          justifyContent: "space-between",
        },
      }}
      {...rest}
    >
      <div
        className={css({
          top: -168,
          position: "absolute",
          transform: "rotate(90deg)",
          [bp.ONLY_MOBILE]: { display: "none" },
          [bp.FROM_TABLET]: { left: "-20vw" },
          [bp.FROM_DESKTOP]: { left: "-17.5vw" },
          [bp.FROM_LARGE_DESKTOP]: {
            left: "-20vw",
          },
          nested: {
            "@media (min-width: 1800px)": { left: "-25vw" },
          },
        })}
      >
        <Oval />
      </div>

      <div
        className={css({
          flex: 1,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          [bp.FROM_DESKTOP]: {
            marginBottom: 45,
          },
        })}
      >
        <div
          className={css({
            width: "100%",
            marginBottom: 20,
            transform: "translateX(-7.5%)",
            [bp.ONLY_TABLET]: {
              width: "60%",
            },
            [bp.FROM_DESKTOP]: {
              maxWidth: 430,
              marginBottom: 0,
              marginRight: "auto",
              transform: "translateY(0)",
            },
            [bp.FROM_LARGE_DESKTOP]: { maxWidth: 460 },
          })}
        >
          <img
            className={css({ maxWidth: "100%" })}
            src="/svgs/illustration.svg"
            alt="Magician with shield"
          />
        </div>
      </div>
      <div
        className={css({
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          [bp.FROM_DESKTOP]: { alignItems: "flex-start" },
        })}
      >
        <Heading as="h2">{title}</Heading>

        {elements && (
          <ul
            className={css({
              margin: 0,
              padding: 0,
              listStyleType: "none",
            })}
          >
            {elements.map((element, index) => (
              <ListElementComponent
                {...element}
                key={`ListElement-${element.id}-${index}`}
              />
            ))}
          </ul>
        )}

        <div
          className={css({
            width: "100%",
            marginTop: 30,
            display: "flex",
            flexDirection: "column",
            [bp.FROM_TABLET]: {
              marginTop: 50,
              flexDirection: "row",
              justifyContent: "center",
            },
            [bp.FROM_DESKTOP]: {
              marginTop: 65,
              marginLeft: 10,
              justifyContent: "flex-start",
            },
          })}
        >
          {cta && (
            <LinkButton
              customStyle={{ marginTop: 45, width: "auto" }}
              {...cta}
            />
          )}
        </div>
      </div>
    </Grid>
  );
};
